import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { Modal } from '@domgen/base-design-system-2.0/molecules/Modal';
import { ReactNode } from 'react';
import { CircleTickIcon } from '../../../assets/icons/CircleTickIcon';
import { personalisedQuoteCopy } from './content/personalisedQuoteContent';
import { currencyFormatter, wholeDollarFormatter } from '@domgen/shared/utils/currency/formatters';
import { getTypography } from './typography';

const copy = personalisedQuoteCopy.upsellModal;
const { ctaApplianceCare } = personalisedQuoteCopy;
const typography = getTypography(import.meta.env.VITE_BRAND_CODE);

const styles = {
  content: 'flex flex-col gap-space-600 w-full desktop:w-182',
  header: 'flex flex-col gap-space-400',
  heading: `${typography.modalTitle} text-text-headline`,
  body: 'flex flex-col gap-space-600',
  subheading: `${typography.label} text-text-headline`,
  benefits: 'flex flex-col gap-space-300 list-none',
  benefit: `flex flex-row items-center gap-space-200 ${typography.body} text-text-body`,
  promoBenefit: 'text-text-success',
  benefitIcon: 'text-accent-3-500 shrink-0 w-6 h-6 [&>svg]:w-6 [&>svg]:h-6',
  note: `py-space-200 px-space-400 rounded-radius-200 ring-1 ring-inset ring-primary-300 bg-bg-primary ${typography.body} text-text-body`,
  actions: 'flex flex-col gap-space-400 tablet:flex-row',
  action: `flex-1 ${typography.button}`,
};

export type PersonalisedUpsellModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onRegisterOnly: () => void;
  onContinueToCheckout: () => void;
  brand: string;
  applianceType: string;
  repairCost?: number;
  monthlyPrice: number;
  promoText: string | null;
};

const Benefit = ({ children, className = '' }: { children: ReactNode; className?: string }) => (
  <li className={`${styles.benefit} ${className}`}>
    <span className={styles.benefitIcon} aria-hidden="true">
      <CircleTickIcon />
    </span>
    {children}
  </li>
);

const withArticle = (noun: string) => `${/^[aeiou]/i.test(noun) ? 'an' : 'a'} ${noun}`;

export const PersonalisedUpsellModal = ({
  isOpen,
  onClose,
  onRegisterOnly,
  onContinueToCheckout,
  brand,
  applianceType,
  repairCost,
  monthlyPrice,
  promoText,
}: PersonalisedUpsellModalProps) => {
  const heading =
    repairCost === undefined
      ? copy.noCostHeader
      : `On average, ${withArticle(applianceType)} can cost up to ${wholeDollarFormatter.format(repairCost)} to repair`;
  const promoBenefit = promoText
    ? `${promoText.replace(/\*$/, '')}. ${copy.noCommitment}`
    : copy.noCommitment;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h2 className={styles.heading}>{heading}</h2>
          <div className={styles.body}>
            <p className={styles.subheading}>
              For {currencyFormatter.format(monthlyPrice)} a month, {brand} Appliance Care has you
              covered
            </p>
            <ul className={styles.benefits}>
              <Benefit>{copy.unlimitedBenefit}</Benefit>
              <Benefit>A replacement {applianceType} if we can't fix it</Benefit>
              <Benefit className={styles.promoBenefit}>{promoBenefit}</Benefit>
            </ul>
          </div>
        </div>

        <p className={styles.note}>
          {copy.laterNote} <strong>{copy.bestPrice}</strong>
        </p>

        <div className={styles.actions}>
          <div className={styles.action}>
            <Button variant="outline" onClick={onRegisterOnly}>
              {copy.registerOnly}
            </Button>
          </div>
          <div className={styles.action}>
            <Button variant="primary" onClick={onContinueToCheckout}>
              {ctaApplianceCare}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
